import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    icon?: React.ReactNode;
    /** When true and type="password", show a toggle button to reveal the password. */
    showPasswordToggle?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ className, label, error, icon, id, type, showPasswordToggle, ...props }, ref) => {
        const inputId = id || label?.toLowerCase().replace(/\s/g, "-");
        const [revealed, setRevealed] = useState(false);
        const canToggle = showPasswordToggle && type === "password";
        const effectiveType = canToggle && revealed ? "text" : type;

        return (
            <div className="space-y-1.5">
                {label && (
                    <label
                        htmlFor={inputId}
                        className="block text-sm font-medium text-neutral-600 dark:text-neutral-400"
                    >
                        {label}
                    </label>
                )}
                <div className="relative">
                    {icon && (
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500">
                            {icon}
                        </div>
                    )}
                    <input
                        id={inputId}
                        ref={ref}
                        type={effectiveType}
                        className={cn(
                            "flex h-11 w-full rounded-[var(--radius-control)] border px-3.5 text-sm text-neutral-800 placeholder:text-neutral-400 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:placeholder:text-neutral-500",
                            "feedback-focus motion-micro focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:shadow-[0_0_0_3px_rgba(99,102,241,0.08)]",
                            "disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400 dark:disabled:bg-neutral-900 dark:disabled:text-neutral-600",
                            "transition-colors",
                            icon && "pl-10",
                            canToggle && "pr-11",
                            error && "border-error focus:border-error focus:ring-error/20",
                            className,
                        )}
                        style={{
                            backgroundColor: "var(--surface-primary)",
                            borderColor: error ? undefined : "var(--border-default)",
                            color: "var(--text-primary)",
                        }}
                        {...props}
                    />
                    {canToggle && (
                        <button
                            type="button"
                            onClick={() => setRevealed((v) => !v)}
                            className="absolute right-0 top-0 bottom-0 flex w-11 items-center justify-center text-neutral-400 transition-colors hover:text-neutral-700 dark:hover:text-neutral-200"
                            aria-label={revealed ? "Passwort verbergen" : "Passwort anzeigen"}
                            aria-pressed={revealed}
                            tabIndex={-1}
                        >
                            {revealed ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                    )}
                </div>
                {error && <p className="text-xs text-error">{error}</p>}
            </div>
        );
    },
);
Input.displayName = "Input";
